import { useRef } from "react";
import type { MockMessage } from "@/dummy";

/** Structural equality for plain JSON-like data (what a message is made of). */
function sameData(a: unknown, b: unknown): boolean {
  if (Object.is(a, b)) return true;
  if (typeof a !== "object" || typeof b !== "object" || a === null || b === null) return false;
  if (Array.isArray(a) !== Array.isArray(b)) return false;
  const ka = Object.keys(a);
  if (ka.length !== Object.keys(b).length) return false;
  const rb = b as Record<string, unknown>;
  const ra = a as Record<string, unknown>;
  return ka.every((k) => k in rb && sameData(ra[k], rb[k]));
}

/**
 * Re-use the previous object for every message that did not change. The merged
 * list is rebuilt (history mapped, live rows deduped) whenever anything
 * upstream moves, which mints a fresh object per row even though the content
 * is the same; handing those to memoised rows would re-render the whole thread
 * for one incoming message. Returns `prev` itself when nothing changed at all.
 */
export function reuseEqualMessages(prev: MockMessage[], next: MockMessage[]): MockMessage[] {
  if (prev === next) return prev;
  const byId = new Map(prev.map((m) => [m.id, m]));
  let allSame = prev.length === next.length;
  const merged = next.map((m, i) => {
    const old = byId.get(m.id);
    const kept = old && sameData(old, m) ? old : m;
    if (kept !== prev[i]) allSame = false;
    return kept;
  });
  return allSame ? prev : merged;
}

/** `reuseEqualMessages` against the list rendered last time. */
export function useReusedMessages(next: MockMessage[]): MockMessage[] {
  const last = useRef<MockMessage[]>([]);
  const stable = reuseEqualMessages(last.current, next);
  last.current = stable;
  return stable;
}
